/**
 * 画布状态管理（极简自管 store，零依赖）
 *
 * 使用 useSyncExternalStore，避免引入 Zustand。
 *
 * 数据流：
 *   UI -> store.setState (mutation) -> notify 订阅者 -> useSyncExternalStore re-render
 *
 * 历史栈：每次「写操作」会入栈一份当前 nodes/edges 的快照（只对节点和连线）。
 * 视口 / 选区 / 自动保存指示器属于 UI 状态，不入历史。
 */
import { useSyncExternalStore } from 'react';
import type {
  CanvasBackground,
  CanvasEdge,
  CanvasNode,
  CanvasProjectFull,
  CanvasProjectMeta,
  CanvasTheme,
  CanvasViewport,
  SelectionState,
} from '../types';
import { HISTORY_LIMIT, MAX_EDGES_PER_PROJECT, MAX_NODES_PER_PROJECT, ZOOM_MAX, ZOOM_MIN } from '../constants';
import { wouldCreateCycle } from './upstream';
import { computeAutoLayout } from '../autoLayout';
import { canConnectNodes, isAgentLineageEdge, isProgrammaticEdgeAllowed } from '../connectionRules';
import { isNodeInsideGroup } from '../utils';

export interface BoardState {
  // 当前画布
  projectId: string | null;
  name: string;
  viewport: CanvasViewport;
  background: CanvasBackground;
  theme: CanvasTheme;
  nodes: CanvasNode[];
  edges: CanvasEdge[];

  // UI 状态
  selection: SelectionState;
  // dirty=true 表示有未持久化变更
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  // 乐观锁：最后一次加载 / 保存成功时服务端的 project.updatedAt，用于多标签页冲突检测
  baseUpdatedAt: string | null;
  // 保存冲突（其他窗口已修改）：置位后停止自动保存，需刷新或导出备份
  conflicted: boolean;
  // 对齐辅助线（拖拽节点时临时显示，画布坐标；v=垂直线 x 列表，h=水平线 y 列表）
  alignmentGuides: { v: number[]; h: number[] } | null;
  // 链式执行中正在执行的节点 ID 列表
  executingNodeIds: string[];
  // 参考图点选模式：激活后点击画布节点即为指定源节点建立/断开连线（Esc 或再次点击退出）
  referencePicker: { sourceNodeId: string } | null;
  // 空白区鼠标模式：pan=默认拖拽平移画布，select=框选节点（悬浮条切换）
  pointerMode: 'pan' | 'select';

  // 历史（节点 + 连线 + 视口 + 背景快照）
  history: { nodes: CanvasNode[]; edges: CanvasEdge[]; viewport: CanvasViewport; background: CanvasBackground }[];
  historyIndex: number; // 指向当前快照
  // 只读预览态：管理后台复用真实节点渲染时置位，所有编辑动作直接丢弃
  readOnly: boolean;
}

const INITIAL_STATE: BoardState = {
  projectId: null,
  name: '',
  viewport: { x: 0, y: 0, zoom: 1 },
  background: 'grid',
  theme: 'light',
  nodes: [],
  edges: [],
  selection: { nodeIds: [], edgeIds: [] },
  dirty: false,
  saving: false,
  lastSavedAt: null,
  baseUpdatedAt: null,
  conflicted: false,
  alignmentGuides: null,
  executingNodeIds: [],
  referencePicker: null,
  pointerMode: 'pan',
  history: [{ nodes: [], edges: [], viewport: { x: 0, y: 0, zoom: 1 }, background: 'grid' }],
  historyIndex: 0,
  readOnly: false,
};

let state: BoardState = INITIAL_STATE;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function setState(updater: Partial<BoardState> | ((s: BoardState) => Partial<BoardState>)) {
  const patch = typeof updater === 'function' ? updater(state) : updater;
  state = { ...state, ...patch };
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): BoardState {
  return state;
}

export function useBoard<T>(selector: (s: BoardState) => T): T {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return selector(snapshot);
}

export function getBoardState(): BoardState {
  return state;
}

// ---------------- 历史辅助 ----------------

// 防抖窗口：连续的 updateNodeData（打字、滑杆）合并为一条历史；
// 结构性操作（增删节点/连线）仍立即落栈，保证撤销粒度清晰。
const HISTORY_DEBOUNCE_MS = 180;
let historyDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function pushHistorySnapshotNow() {
  // 与栈顶完全一致（已落栈的操作再次 flush，如 undo/redo 入口的 flush）时跳过：
  // 重复入栈会抬高 historyIndex，使 undo 取到与当前相同的快照而永远无效，
  // 且重复项会挤占 HISTORY_LIMIT 丢掉真实历史
  const top = state.history[state.historyIndex];
  if (top && top.nodes === state.nodes && top.edges === state.edges) {
    return;
  }
  const head = state.history.slice(0, state.historyIndex + 1);
  head.push({ nodes: state.nodes, edges: state.edges, viewport: state.viewport, background: state.background });
  while (head.length > HISTORY_LIMIT) head.shift();
  state.history = head;
  state.historyIndex = head.length - 1;
}

function cancelHistoryDebounce() {
  if (historyDebounceTimer !== null) {
    clearTimeout(historyDebounceTimer);
    historyDebounceTimer = null;
  }
}

/** 立即落栈：把防抖窗口内未落栈的编辑合并成一条并清掉定时器 */
function flushHistoryPush() {
  cancelHistoryDebounce();
  pushHistorySnapshotNow();
}

/** 防抖落栈：窗口内的连续写只产生一条历史 */
function pushHistorySnapshotDebounced() {
  cancelHistoryDebounce();
  historyDebounceTimer = setTimeout(() => {
    historyDebounceTimer = null;
    pushHistorySnapshotNow();
    notify();
  }, HISTORY_DEBOUNCE_MS);
}

function pushHistorySnapshot() {
  pushHistorySnapshotNow();
}

/**
 * 图片/图片组节点补稳定图号（refNo）：@图N 引用按它定位，与连线位置无关，
 * 删除其他节点不会漂移。号段从 1 递增、永不回收；已有图号的不动。
 * 用于三个节点创建入口（addNode/addNodes/applyGraphPatch）与旧项目加载回填。
 */
function assignImageRefNos(nodes: CanvasNode[], incoming: CanvasNode[]): CanvasNode[] {
  let max = 0;
  for (const n of nodes) {
    if (n.type !== 'image' && n.type !== 'image_group') continue;
    const refNo = (n.data as Record<string, unknown>).refNo;
    if (typeof refNo === 'number' && refNo > max) max = refNo;
  }
  let changed = false;
  const patched = incoming.map((n) => {
    if (n.type !== 'image' && n.type !== 'image_group') return n;
    if (typeof (n.data as Record<string, unknown>).refNo === 'number') return n;
    changed = true;
    return { ...n, data: { ...n.data, refNo: ++max } as CanvasNode['data'] };
  });
  return changed ? patched : incoming;
}

function buildLoadedState(project: CanvasProjectFull, readOnly: boolean): BoardState {
  const vp = { x: project.viewportX, y: project.viewportY, zoom: project.zoom };
  // 旧项目快照里的图片/图片组可能没有图号（refNo），加载时一次性回填
  let nodes = assignImageRefNos([], project.nodes);
  // generating 是运行时瞬态标记（前端轮询进度用），不应跨刷新持久化——
  // 刷新后轮询已断、executingNodeIds 已清空，残留的 generating=true 会让按钮
  // 永久禁用或因快照未及时保存而导致状态不一致。加载时统一清除。
  nodes = nodes.map((n) =>
    (n.data as Record<string, unknown>)?.generating
      ? { ...n, data: { ...n.data, generating: false } as CanvasNode['data'] }
      : n
  );
  return {
    ...INITIAL_STATE,
    projectId: project.id,
    name: project.name,
    viewport: vp,
    background: project.background,
    theme: project.theme,
    nodes,
    edges: project.edges,
    history: [{ nodes, edges: project.edges, viewport: vp, background: project.background }],
    historyIndex: 0,
    dirty: false,
    saving: false,
    lastSavedAt: Date.now(),
    baseUpdatedAt: project.updatedAt ?? null,
    conflicted: false,
    executingNodeIds: [],
    referencePicker: null,
    pointerMode: 'pan',
    selection: { nodeIds: [], edgeIds: [] },
    readOnly,
  };
}

const mutableBoardActions = {
  reset() {
    cancelHistoryDebounce();
    state = { ...INITIAL_STATE, history: [{ nodes: [], edges: [], viewport: { x: 0, y: 0, zoom: 1 }, background: 'grid' }], historyIndex: 0 };
    notify();
  },

  loadProject(project: CanvasProjectFull) {
    cancelHistoryDebounce();
    state = buildLoadedState(project, false);
    notify();
  },

  /**
   * 只读预览：管理后台复用真实节点组件渲染别人的画布。
   * 与 loadProject 使用同一套归一化逻辑，但置 readOnly，
   * 之后所有编辑/派生写回都会被 boardActions 代理丢弃。
   */
  loadPreview(project: CanvasProjectFull) {
    cancelHistoryDebounce();
    state = buildLoadedState(project, true);
    notify();
  },

  setProjectMeta(patch: Partial<Pick<CanvasProjectMeta, 'name' | 'background' | 'theme'>>) {
    setState({ ...patch, dirty: true });
  },

  setViewport(viewport: Partial<CanvasViewport>) {
    const next = { ...state.viewport, ...viewport };
    if (typeof next.zoom === 'number') {
      next.zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next.zoom));
    }
    setState({ viewport: next, dirty: true });
  },

  resetViewport() {
    setState({ viewport: { x: 0, y: 0, zoom: 1 }, dirty: true });
  },

  // ---------------- 节点 ----------------

  addNode(node: CanvasNode) {
    if (state.nodes.length >= MAX_NODES_PER_PROJECT) {
      console.warn('[boardStore] 节点数已达上限', MAX_NODES_PER_PROJECT);
      return;
    }
    const [assigned] = assignImageRefNos(state.nodes, [node]);
    state = {
      ...state,
      nodes: [...state.nodes, assigned],
      selection: { nodeIds: [node.id], edgeIds: [] },
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  /** 批量添加节点和连线（粘贴等场景），只产生一条历史 */
  addNodes(nodes: CanvasNode[], edges: CanvasEdge[] = []) {
    if (state.nodes.length + nodes.length > MAX_NODES_PER_PROJECT) {
      console.warn('[boardStore] 节点数将超出上限，已跳过', MAX_NODES_PER_PROJECT);
      return;
    }
    if (state.edges.length + edges.length > MAX_EDGES_PER_PROJECT) {
      console.warn('[boardStore] 连线数将超出上限，已跳过', MAX_EDGES_PER_PROJECT);
      return;
    }
    // 防御性校验：程序化边只放行「手动合法 ∪ 系统血缘」形态（见 connectionRules），
    // 防止调用方 bug 静默写入不可消费的死边
    const typeById = new Map([...state.nodes, ...nodes].map((n) => [n.id, n.type]));
    const validEdges = edges.filter((e) => {
      const st = typeById.get(e.sourceNodeId);
      const tt = typeById.get(e.targetNodeId);
      if (!st || !tt) return false;
      if (!isProgrammaticEdgeAllowed(st, tt)) {
        console.warn('[boardStore] 程序化边类型不合法，跳过', st, '->', tt);
        return false;
      }
      return true;
    });
    state = {
      ...state,
      nodes: [...state.nodes, ...assignImageRefNos(state.nodes, nodes)],
      edges: [...state.edges, ...validEdges],
      selection: { nodeIds: nodes.map((n) => n.id), edgeIds: [] },
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  updateNode(id: string, patch: Partial<CanvasNode>) {
    state = {
      ...state,
      nodes: state.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
      dirty: true,
    };
    notify();
  },

  // 拖拽 / 缩放结束等场景：先 updateNode 多次（不入历史），结束时 commit 一次
  commitNodeMutation() {
    // 立即落栈（合并防抖窗口内的突发编辑），拖拽全程只产生一条历史
    flushHistoryPush();
    notify();
  },

  updateNodeData(id: string, dataPatch: Record<string, unknown>) {
    state = {
      ...state,
      nodes: state.nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...dataPatch } as CanvasNode['data'] } : n
      ),
      dirty: true,
    };
    pushHistorySnapshotDebounced();
    notify();
  },

  // 派生校正（默认模型回填、quality 按模型能力收敛等）：写数据但不置 dirty，
  // 避免打开项目未做任何编辑就触发一次自动保存
  updateNodeDataSilent(id: string, dataPatch: Record<string, unknown>) {
    state = {
      ...state,
      nodes: state.nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...dataPatch } as CanvasNode['data'] } : n
      ),
    };
    notify();
  },

  removeNodes(ids: string[]) {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    state = {
      ...state,
      nodes: state.nodes.filter((n) => !idSet.has(n.id)),
      edges: state.edges.filter((e) => !idSet.has(e.sourceNodeId) && !idSet.has(e.targetNodeId)),
      selection: { nodeIds: [], edgeIds: [] },
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  /**
   * 原子图补丁：一次状态更新内完成 加/删节点、加/删边、改节点数据，
   * 只产生一条撤销历史。供"多对象联动且需单步撤销"的操作使用
   * （图片聚合为组 / 从组取出图片等）。边的类型合法性做防御校验
   * （手动合法 ∪ 系统血缘，见 connectionRules），其余出入参由调用方保证。
   */
  applyGraphPatch(patch: {
    addNodes?: CanvasNode[];
    removeNodeIds?: string[];
    addEdges?: CanvasEdge[];
    removeEdgeIds?: string[];
    nodeDataPatches?: Array<{ id: string; data: Record<string, unknown> }>;
  }) {
    const {
      addNodes = [],
      removeNodeIds = [],
      addEdges = [],
      removeEdgeIds = [],
      nodeDataPatches = [],
    } = patch;
    if (
      addNodes.length === 0 &&
      removeNodeIds.length === 0 &&
      addEdges.length === 0 &&
      removeEdgeIds.length === 0 &&
      nodeDataPatches.length === 0
    ) {
      return;
    }

    const removeNodeIdSet = new Set(removeNodeIds);
    const removeEdgeIdSet = new Set(removeEdgeIds);
    const dataPatchMap = new Map(nodeDataPatches.map((p) => [p.id, p.data]));

    // 语义对齐 removeNodes：被删节点的悬挂边一并清理
    const edgesAfterRemove = state.edges.filter(
      (e) =>
        !removeEdgeIdSet.has(e.id) &&
        !removeNodeIdSet.has(e.sourceNodeId) &&
        !removeNodeIdSet.has(e.targetNodeId)
    );
    // 去重：与现存/本批重复的同向边不重复添加
    const existingPairs = new Set(
      edgesAfterRemove.map((e) => `${e.sourceNodeId}->${e.targetNodeId}`)
    );
    const newEdges = addEdges.filter((e) => {
      const key = `${e.sourceNodeId}->${e.targetNodeId}`;
      if (existingPairs.has(key)) return false;
      existingPairs.add(key);
      return true;
    });

    const nodesAfterRemove = state.nodes.filter((n) => !removeNodeIdSet.has(n.id));
    const nodeIdSet = new Set([...nodesAfterRemove, ...addNodes].map((n) => n.id));
    // 新建图片/图片组补稳定图号（在删除后的存量节点基础上取最大值递增）
    const assignedAddNodes = assignImageRefNos(nodesAfterRemove, addNodes);
    const nodeTypeById = new Map(
      [...nodesAfterRemove, ...assignedAddNodes].map((n) => [n.id, n.type] as const)
    );
    const validNewEdges = newEdges.filter((e) => {
      if (!nodeIdSet.has(e.sourceNodeId) || !nodeIdSet.has(e.targetNodeId)) return false;
      const st = nodeTypeById.get(e.sourceNodeId);
      const tt = nodeTypeById.get(e.targetNodeId);
      if (!st || !tt) return false;
      // 防御性校验：程序化边只放行「手动合法 ∪ 系统血缘」形态
      if (!isProgrammaticEdgeAllowed(st, tt)) {
        console.warn('[boardStore] 图补丁边类型不合法，跳过', st, '->', tt);
        return false;
      }
      return true;
    });

    state = {
      ...state,
      nodes: [
        ...nodesAfterRemove.map((n) =>
          dataPatchMap.has(n.id) ? { ...n, data: { ...n.data, ...dataPatchMap.get(n.id) } } : n
        ),
        ...assignedAddNodes,
      ],
      edges: [...edgesAfterRemove, ...validNewEdges],
      // 选区指向新建节点（没有新增时清空，避免选中已删除节点）
      selection: {
        nodeIds: assignedAddNodes.map((n) => n.id),
        edgeIds: [],
      },
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  // ---------------- 连线 ----------------

  addEdge(edge: CanvasEdge) {
    const sourceNode = state.nodes.find((n) => n.id === edge.sourceNodeId);
    const targetNode = state.nodes.find((n) => n.id === edge.targetNodeId);
    if (!sourceNode || !targetNode) {
      console.warn('[boardStore] 连线节点不存在，跳过', edge);
      return;
    }
    // 节点级判定：类型表 + 动态能力（video→生视频需目标模型+模式支持参考视频）
    const connectCheck = canConnectNodes(sourceNode, targetNode);
    if (!connectCheck.ok) {
      console.warn('[boardStore] 连线不合法，跳过：', connectCheck.reason);
      return;
    }
    // 避免重复连线（同 source/target 只保留一条）
    const exists = state.edges.some(
      (e) => e.sourceNodeId === edge.sourceNodeId && e.targetNodeId === edge.targetNodeId
    );
    if (exists) {
      console.warn('[boardStore] 连线已存在，跳过', edge);
      return;
    }
    // 避免反向重复（A→B 存在时不允许 B→A）
    const reverseExists = state.edges.some(
      (e) => e.sourceNodeId === edge.targetNodeId && e.targetNodeId === edge.sourceNodeId
    );
    if (reverseExists) {
      console.warn('[boardStore] 反向连线已存在，跳过', edge);
      return;
    }
    // 环检测：禁止形成循环连接
    if (wouldCreateCycle(edge.sourceNodeId, edge.targetNodeId, state.edges)) {
      console.warn('[boardStore] 检测到循环连接，跳过', edge);
      return;
    }
    if (state.edges.length >= MAX_EDGES_PER_PROJECT) {
      console.warn('[boardStore] 连线数已达上限', MAX_EDGES_PER_PROJECT);
      return;
    }
    state = {
      ...state,
      edges: [...state.edges, edge],
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  /**
   * 删除连线。智能体批次血缘边（isAgentLineageEdge）不可手动断开：
   * 自动过滤后仅删可删部分并返回计数，全部被拦截时不产生任何状态变更
   * （不推历史），由调用方按 blocked 提示用户。程序化改接（组内取出图片
   * 的血缘跟随）走 applyGraphPatch，不经此入口。
   */
  removeEdges(ids: string[]): { removed: number; blocked: number } {
    if (ids.length === 0) return { removed: 0, blocked: 0 };
    const idSet = new Set(ids);
    const touched = state.edges.filter((e) => idSet.has(e.id));
    const removable = touched.filter((e) => !isAgentLineageEdge(e, state.nodes));
    const blocked = touched.length - removable.length;
    if (removable.length === 0) return { removed: 0, blocked };
    const removableIds = new Set(removable.map((e) => e.id));
    state = {
      ...state,
      edges: state.edges.filter((e) => !removableIds.has(e.id)),
      selection: { ...state.selection, edgeIds: [] },
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
    return { removed: removable.length, blocked };
  },

  clearCanvas() {
    if (state.nodes.length === 0 && state.edges.length === 0) return;
    state = {
      ...state,
      nodes: [],
      edges: [],
      selection: { nodeIds: [], edgeIds: [] },
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  autoLayout() {
    if (state.nodes.length === 0) return;
    // 分组容器与其成员不参与自动布局（成员移动会脱离分组，破坏包含关系）
    const groups = state.nodes.filter((n) => n.type === 'group');
    const memberIds = new Set<string>();
    groups.forEach((g) => {
      state.nodes.forEach((n) => {
        if (isNodeInsideGroup(n, g)) memberIds.add(n.id);
      });
    });
    const layoutNodes = state.nodes.filter((n) => n.type !== 'group' && !memberIds.has(n.id));
    if (layoutNodes.length === 0) return;
    const layoutIds = new Set(layoutNodes.map((n) => n.id));
    const layoutEdges = state.edges.filter(
      (e) => layoutIds.has(e.sourceNodeId) && layoutIds.has(e.targetNodeId)
    );
    const positions = computeAutoLayout(layoutNodes, layoutEdges);
    const posMap = new Map(positions.map((p) => [p.id, p]));
    state = {
      ...state,
      nodes: state.nodes.map((n) => {
        const pos = posMap.get(n.id);
        return pos ? { ...n, x: pos.x, y: pos.y } : n;
      }),
      dirty: true,
    };
    pushHistorySnapshot();
    notify();
  },

  // ---------------- 选区 ----------------

  setSelection(selection: SelectionState) {
    setState({ selection });
  },

  selectAll() {
    setState({
      selection: {
        nodeIds: state.nodes.map((n) => n.id),
        edgeIds: state.edges.map((e) => e.id),
      },
    });
  },

  clearSelection() {
    setState({ selection: { nodeIds: [], edgeIds: [] } });
  },

  // ---------------- 参考图点选模式 ----------------

  setPointerMode(mode: 'pan' | 'select') {
    if (state.pointerMode !== mode) setState({ pointerMode: mode });
  },

  startReferencePick(sourceNodeId: string) {
    setState({ referencePicker: { sourceNodeId } });
  },

  cancelReferencePick() {
    if (state.referencePicker) setState({ referencePicker: null });
  },

  // ---------------- 历史 ----------------

  undo() {
    // 先把防抖窗口内的编辑落为一条历史，保证撤销后可重做
    flushHistoryPush();
    if (state.historyIndex <= 0) return;
    const idx = state.historyIndex - 1;
    const snap = state.history[idx];
    state = {
      ...state,
      nodes: snap.nodes,
      edges: snap.edges,
      // 视口是观察视角而非画布内容，不随 undo 跳变；background 属内容属性保留恢复
      background: snap.background,
      historyIndex: idx,
      selection: { nodeIds: [], edgeIds: [] },
      dirty: true,
    };
    notify();
  },

  redo() {
    flushHistoryPush();
    if (state.historyIndex >= state.history.length - 1) return;
    const idx = state.historyIndex + 1;
    const snap = state.history[idx];
    state = {
      ...state,
      nodes: snap.nodes,
      edges: snap.edges,
      background: snap.background,
      historyIndex: idx,
      selection: { nodeIds: [], edgeIds: [] },
      dirty: true,
    };
    notify();
  },

  // ---------------- 自动保存 ----------------

  markSaving(saving: boolean) {
    setState({ saving });
  },

  markDirty() {
    setState({ dirty: true });
  },

  // 同步乐观锁基准（重命名等 PATCH 操作会推进服务端 updatedAt，需同步避免自我误判冲突）
  setBaseUpdatedAt(updatedAt: string) {
    setState({ baseUpdatedAt: updatedAt });
  },

  markSaved(updatedAt?: string) {
    setState({
      dirty: false,
      saving: false,
      // 保存成功即视为冲突解除（覆盖保存 / 重试成功的场景都要撤下冲突横幅）
      conflicted: false,
      lastSavedAt: Date.now(),
      ...(updatedAt ? { baseUpdatedAt: updatedAt } : {}),
    });
  },

  // 保存冲突（其他窗口已修改）：停止自动保存，等待用户处理
  markConflicted() {
    setState({ conflicted: true, saving: false });
  },

  // 冲突后主动放弃本地未保存修改（配合整页重新加载服务端最新版本）
  discardUnsaved() {
    setState({ dirty: false, saving: false, conflicted: false });
  },

  setExecutingNodeIds(ids: string[]) {
    setState({ executingNodeIds: ids });
  },

  // 对齐辅助线：仅 UI 状态，不入历史、不置 dirty。
  // 拖拽期间每次 pointermove 都会调用；结构未变化时跳过 setState，
  // 避免与 updateNode 叠加造成每帧两轮全量渲染（拖拽不跟手的元凶之一）
  setAlignmentGuides(guides: { v: number[]; h: number[] } | null) {
    const cur = state.alignmentGuides;
    const unchanged =
      (cur === null && guides === null) ||
      (cur !== null &&
        guides !== null &&
        cur.v.length === guides.v.length &&
        cur.h.length === guides.h.length &&
        cur.v.every((v, i) => v === guides.v[i]) &&
        cur.h.every((v, i) => v === guides.h[i]));
    if (unchanged) return;
    setState({ alignmentGuides: guides });
  },
};

/** 只读预览态下仍允许执行的动作：加载 / 重置预览态本身 */
const READ_ONLY_ALLOWED_ACTIONS = new Set(['reset', 'loadProject', 'loadPreview']);

export type BoardActions = typeof mutableBoardActions;

/**
 * 动作代理：readOnly 只读预览态下丢弃所有编辑与派生写回。
 * 后台预览复用真实节点组件时，节点挂载副作用（模型回退、智能体外框、
 * 尺寸回写等）不会改动预览数据，也不会留下可保存的脏状态。
 */
export const boardActions = new Proxy(mutableBoardActions, {
  get(target, prop, receiver) {
    const value = Reflect.get(target, prop, receiver);
    if (typeof value !== 'function') return value;
    if (READ_ONLY_ALLOWED_ACTIONS.has(String(prop))) return value;
    return (...args: unknown[]) => {
      if (state.readOnly) return undefined;
      return (value as (...a: unknown[]) => unknown).apply(target, args);
    };
  },
}) as BoardActions;
